import { useState } from "react";
import { Check, FileText, X } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "@/components/ui/sheet";
import { useRegistroDeTerreno, useRevisarNotasGenerales } from "@/hooks/useRegistroDeTerreno";
import { getApiErrorMessage } from "@/lib/api";
import type { AudioDeTerreno, NotasGeneralesDeTerreno } from "@/lib/terreno";
import { cn } from "@/lib/utils";

import { BotonDescartarDeTerreno } from "./DescarteDeTerreno";
import { ID_DE_LAS_NOTAS_GENERALES, cantidadDeNotas, metaDeLasNotas } from "./notasGenerales";
import { ReproductorDeAudioDeTerreno } from "./ReproductorDeAudioDeTerreno";
import { avisoDeTranscripcion, horaDeCaptura } from "./registroDeTerreno";

async function copiar(texto: string, aviso: string) {
  try {
    await navigator.clipboard.writeText(texto);
    toast.success(aviso);
  } catch {
    toast.error("No pudimos copiar al portapapeles.");
  }
}

/** El rótulo en versalitas con que abre cada tarjeta. */
function Rotulo({ children }: { children: React.ReactNode }) {
  return (
    <span className="text-ink-muted text-[0.5625rem] font-extrabold tracking-[0.07em] uppercase">
      {children}
    </span>
  );
}

function BotonCopiar({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-success-soft-foreground hover:text-ink focus-visible:ring-ring/50 ml-auto rounded-sm text-[0.65625rem] font-bold outline-none focus-visible:ring-[3px]"
    >
      Copiar
    </button>
  );
}

const TARJETA = "border-border bg-card rounded-[0.6875rem] border px-[0.6875rem] pt-[0.5625rem] pb-2.5";
const TEXTO = "text-[0.78125rem] leading-[1.55] font-medium";

function TarjetaDeAudioGeneral({ audio, informeId }: { audio: AudioDeTerreno; informeId: number }) {
  const lista = audio.transcripcion_estado === "lista" && audio.transcripcion;

  return (
    <div className={TARJETA}>
      <ReproductorDeAudioDeTerreno informeId={informeId} audio={audio} compacto />
      <div className="mt-2 flex items-center gap-2">
        <Rotulo>Transcripción</Rotulo>
        <span className="text-ink-hint font-mono text-[0.625rem] font-semibold">
          {horaDeCaptura(audio.capturado_en)}
        </span>
        <BotonDescartarDeTerreno uuid={audio.uuid} etiqueta="nota de voz · Notas generales" />
        {lista ? (
          <BotonCopiar onClick={() => void copiar(audio.transcripcion!, "Transcripción copiada")} />
        ) : null}
      </div>
      {lista ? (
        <p className={cn(TEXTO, "text-ink-body mt-1 text-pretty")}>{audio.transcripcion}</p>
      ) : (
        <p className={cn(TEXTO, "text-ink-hint mt-1 italic")}>{avisoDeTranscripcion(audio)}</p>
      )}
    </div>
  );
}

/**
 * El material de las notas generales: el texto, cada audio con su
 * transcripción y, si no hay nada, el aviso. Lo comparten el cajón de la
 * cabecera y la tarjeta de la pestaña de terreno.
 */
export function CuerpoDeLasNotasGenerales({
  informeId,
  notas,
}: {
  informeId: number;
  notas: NotasGeneralesDeTerreno;
}) {
  if (cantidadDeNotas(notas) === 0) {
    return (
      <div className="text-ink-muted rounded-xl border-[1.5px] border-dashed p-4 text-center text-[0.78125rem] font-semibold">
        El encargado no registró notas generales en terreno.
      </div>
    );
  }

  return (
    <>
      {notas.texto ? (
        <div className={TARJETA}>
          <div className="flex items-center gap-2">
            <Rotulo>Texto</Rotulo>
            <BotonCopiar onClick={() => void copiar(notas.texto!.texto, "Texto copiado")} />
            <BotonDescartarDeTerreno uuid={notas.texto.uuid} etiqueta="texto · Notas generales" />
          </div>
          <p className={cn(TEXTO, "text-ink-body mt-1 whitespace-pre-line")}>{notas.texto.texto}</p>
        </div>
      ) : null}
      {notas.audios.map((audio) => (
        <TarjetaDeAudioGeneral key={audio.uuid} audio={audio} informeId={informeId} />
      ))}
    </>
  );
}

/** El interruptor «Revisadas»: una casilla nativa (accesible) dibujada como chip. */
export function ChipRevisadas({
  revisadas,
  deshabilitada,
  onCambiar,
}: {
  revisadas: boolean;
  deshabilitada: boolean;
  onCambiar: (valor: boolean) => void;
}) {
  return (
    <label
      className={cn(
        "has-focus-visible:ring-ring/50 inline-flex items-center gap-2 rounded-lg border-[1.5px] px-3 py-1.5 text-xs font-bold has-focus-visible:ring-[3px]",
        revisadas
          ? "border-primary/40 bg-success-soft text-success-soft-foreground"
          : "border-ink-ghost bg-card text-ink",
        deshabilitada ? "cursor-not-allowed opacity-50" : "cursor-pointer",
      )}
    >
      <input
        type="checkbox"
        className="peer sr-only"
        checked={revisadas}
        disabled={deshabilitada}
        onChange={(e) => onCambiar(e.target.checked)}
      />
      <span
        aria-hidden="true"
        className={cn(
          "flex size-[0.9375rem] flex-none items-center justify-center rounded-[0.3125rem] border-[1.5px]",
          revisadas ? "border-primary bg-primary text-white" : "border-ink-ghost bg-card",
        )}
      >
        {revisadas ? <Check className="size-2.5" strokeWidth={3.5} /> : null}
      </span>
      Revisadas
    </label>
  );
}

/** El chip ya cableado: escribe en el servidor y avisa si no pudo. */
function ChipRevisadasDelInforme({
  informeId,
  notas,
}: {
  informeId: number;
  notas: NotasGeneralesDeTerreno;
}) {
  const revisar = useRevisarNotasGenerales(informeId);

  return (
    <ChipRevisadas
      revisadas={notas.revisadas}
      deshabilitada={cantidadDeNotas(notas) === 0}
      onCambiar={(valor) =>
        revisar.mutate(valor, {
          onError: (error) =>
            toast.error(getApiErrorMessage(error, "No pudimos guardar el cambio. Inténtalo de nuevo.")),
        })
      }
    />
  );
}

function IconoDeNotas() {
  return (
    <span className="bg-info-soft text-info-soft-foreground flex size-[1.875rem] flex-none items-center justify-center rounded-[0.5625rem]">
      <FileText className="size-4" />
    </span>
  );
}

/**
 * La primera tarjeta de la pestaña de terreno: las notas del PROYECTO, antes de
 * las secciones porque no pertenecen a ninguna.
 */
export function TarjetaDeNotasGenerales({
  informeId,
  encargado,
  notas,
}: {
  informeId: number;
  encargado: string | null;
  notas: NotasGeneralesDeTerreno;
}) {
  return (
    <section
      id={ID_DE_LAS_NOTAS_GENERALES}
      aria-label="Notas generales del proyecto"
      className="border-border bg-card scroll-mt-5 rounded-xl border-[1.5px] px-5 pt-[1.125rem] pb-5"
    >
      <div className="flex flex-wrap items-center gap-3">
        <IconoDeNotas />
        <div className="min-w-0">
          <h3 className="text-ink text-[0.9375rem] font-bold tracking-[-0.01em]">
            Notas generales del proyecto
          </h3>
          <p className="text-ink-muted mt-px text-xs font-semibold">
            {metaDeLasNotas(encargado, notas)}
          </p>
        </div>
        <div className="ml-auto">
          <ChipRevisadasDelInforme informeId={informeId} notas={notas} />
        </div>
      </div>
      <div className="mt-3.5 flex max-w-[57.5rem] flex-col gap-2">
        <CuerpoDeLasNotasGenerales informeId={informeId} notas={notas} />
      </div>
    </section>
  );
}

type EstadoDelBoton = "vacio" | "pendiente" | "revisadas";

const SIN_NOTAS: NotasGeneralesDeTerreno = {
  texto: null,
  audios: [],
  revisadas: false,
  revisadas_en: null,
};

/**
 * La puerta de la cabecera a las notas generales: un botón con el contador y
 * un cajón de solo lectura. El contador es verde mientras haya algo por mirar
 * y gris cuando ya se revisó.
 */
export function BotonDeNotasGenerales({
  informeId,
  onVerEnRegistro,
}: {
  informeId: number;
  /** Lleva a la pestaña de terreno; el cajón se cierra antes. */
  onVerEnRegistro: () => void;
}) {
  const consulta = useRegistroDeTerreno(informeId);
  const [abierto, setAbierto] = useState(false);

  const notas = consulta.data?.notas_generales ?? SIN_NOTAS;
  const encargado = consulta.data?.encargado?.nombre ?? null;
  const cantidad = cantidadDeNotas(notas);
  const estado: EstadoDelBoton =
    cantidad === 0 ? "vacio" : notas.revisadas ? "revisadas" : "pendiente";

  return (
    <>
      <Button
        type="button"
        variant="outline"
        data-estado={estado}
        disabled={!consulta.data}
        title="Notas generales del proyecto, desde la App de terreno"
        onClick={() => setAbierto(true)}
        className={cn(
          "h-auto rounded-xl px-[0.8125rem] py-[0.5625rem] text-[0.78125rem] font-bold",
          estado === "pendiente" && "border-primary/40 bg-success-soft hover:bg-success-soft",
        )}
      >
        <FileText className="size-[0.9375rem]" />
        Notas generales
        {cantidad > 0 ? (
          <span
            className={cn(
              "inline-flex min-h-[1.125rem] min-w-[1.125rem] items-center justify-center rounded-full px-1 font-mono text-[0.65625rem] font-extrabold text-white",
              notas.revisadas ? "bg-ink-hint" : "bg-primary",
            )}
          >
            {cantidad}
          </span>
        ) : null}
      </Button>

      <Sheet open={abierto} onOpenChange={setAbierto}>
        <SheetContent
          showCloseButton={false}
          overlayClassName="bg-[rgba(23,24,27,0.22)]"
          className="bg-superficie-sutil w-full gap-0 p-0 sm:max-w-[30rem]"
        >
          <div className="border-border bg-card flex items-center gap-3 border-b px-5 py-4">
            <IconoDeNotas />
            <div className="min-w-0 flex-1">
              <SheetTitle className="text-ink text-[0.9375rem] font-bold">Notas generales</SheetTitle>
              <SheetDescription className="text-ink-muted mt-px text-[0.71875rem] font-semibold">
                {metaDeLasNotas(encargado, notas)}
              </SheetDescription>
            </div>
            <SheetClose
              aria-label="Cerrar"
              className="bg-muted text-ink-muted hover:text-ink focus-visible:ring-ring/50 flex size-8 flex-none items-center justify-center rounded-[0.625rem] outline-none focus-visible:ring-[3px]"
            >
              <X className="size-4" />
            </SheetClose>
          </div>

          <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto px-5 pt-4 pb-6">
            <p className="text-ink-body text-[0.71875rem] font-semibold text-pretty">
              Lo que el encargado registró del proyecto en general, fuera de las secciones. Se
              entrega a la IA al compilar el informe. Solo lectura.
            </p>
            <CuerpoDeLasNotasGenerales informeId={informeId} notas={notas} />
          </div>

          <div className="border-border bg-card flex items-center justify-between gap-3 border-t px-5 py-3">
            <ChipRevisadasDelInforme informeId={informeId} notas={notas} />
            <button
              type="button"
              onClick={() => {
                setAbierto(false);
                onVerEnRegistro();
              }}
              className="text-success-soft-foreground hover:text-ink focus-visible:ring-ring/50 rounded-sm text-xs font-bold outline-none focus-visible:ring-[3px]"
            >
              Ver en Registro de terreno
            </button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
